import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./index.css";
import { useNavigate } from "react-router-dom";
import ReshootPopup from "./reshoot";
import { imageUpload, submitSimmetryScan, simmetryImageUpload } from "../../../Services/APIs/scan";
import dataURItoBlob from "../../../Utils/dataUriToBlob";
import { encrypt } from "../../../Utils/hmac";
import { getCroppedImg } from "../../../Utils/cropImageHelper";
import { postLogDetails } from '../../../Services/APIs/appDetails';
import oval from "../../../assets/Icons/oval.png"
import ImageProcessing from "../../../assets/simmetry/image-processing.png";
import TeethWhitening from "../../../assets/simmetry/teeth-whitening.png";
import ServerBusyPopup from "./serverBusyPopup";

const Preview = (props) => {
    const { image, setCamera, onClose, setDemo } = props;
    const { uid, company } = useSelector(state => state.smileWhite);
    const [isServerBusy, setIsServerBusy] = useState(false);
    const dispatch = useDispatch();
    const [isLoading, setLoading] = useState(false);
    const [reshoot, setReshoot] = useState(false);
    const [isSuccess, setSuccess] = useState(false);
    const [errorType, setImageError] = useState('')
    const [showErrorPopup, setShowErrorPopup] = useState(false); // New state for error popup
    const navigate = useNavigate();
    const folderName = uid || "";
    const [cropStatus, setCropStatus] = useState(false);
    const [croppedImage, setCroppedImage] = useState(null);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [showFullPreview, setShowFullPreview] = useState(false); // New state for full-screen preview
    const [croppedBlob, setCroppedBlob] = useState();
    const [progress, setProgress] = useState(10); // Start at 10%
    const [isActive, setIsActive] = useState(false);
    const [isApiError, setIsApiError] = useState(true);
    const [info, setInfo] = useState("Checking for photo quality...")
    const [showBookButton, setShowBookButton] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(() => {
        return localStorage.getItem("is_form") === "true";
    });

    const toggleActiveState = () => {
        localStorage.setItem("is_form", "true");
        setIsFormSubmitted(true); // Update React state
        setProgress(10);
    };

    // Function to check if running in iframe
    const isInIframe = () => {
        return window.self !== window.top;
    };

    // Function to extract URL parameters from current page
    const getUrlParameters = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const params = {};
        
        // Get all parameters from the URL
        for (const [key, value] of urlParams.entries()) {
            params[key] = value;
        }
        
        return params;
    };

    // Function to build booking URL with all parameters
    const buildBookingUrl = () => {
        const baseUrl = "https://booking.smilewhite.co.uk/booking/";
        const params = getUrlParameters();
        
        // Ensure uid is included
        if (uid && !params.uid) {
            params.uid = uid;
        }
        
        // Build query string
        const queryString = new URLSearchParams(params).toString();
        
        return `${baseUrl}?${queryString}`;
    };

    // Function to handle booking button click
    const handleBookingClick = () => {
        const bookingUrl = buildBookingUrl();
        postLogDetails({ uid, payLoad: `Opening booking URL: ${bookingUrl}` });
        
        try {
            // Force opening in parent window context
            if (isInIframe()) {
                postLogDetails({ uid, payLoad: `In iframe - using parent.open` });
                window.parent.open(bookingUrl, '_blank');
            } else {
                postLogDetails({ uid, payLoad: `Not in iframe - using window.open` });
                window.open(bookingUrl, '_blank');
            }
        } catch (error) {
            postLogDetails({ uid, payLoad: `Error opening booking URL: ${error.message}` });
            // Fallback - try direct assignment
            if (isInIframe()) {
                window.parent.location.href = bookingUrl;
            } else {
                window.location.href = bookingUrl;
            }
        }
    };

    const sendMessageToParent = (payload) => {
        postLogDetails({ uid, payLoad: `sendMessageToParent called with result: ${payload.result}` });
        
        // Always try to open booking URL when sending message to parent
        // handleBookingClick();
            setShowBookButton(true)
        if (window.self !== window.top) {
            const isSuccess = payload.result === true;

            const message = {
                type: isSuccess ? "IMAGE_VALIDATION_SUCCESS" : "IMAGE_VALIDATION_FAILURE",
                message: isSuccess
                    ? "Image validation is successful"
                    : payload?.error?.message || "Image validation failed",
                result: payload.result,
                ...(isSuccess ? {} : { error: payload.error }),
                timestamp: new Date().toISOString()
            };

            try {
                window.parent.postMessage(JSON.stringify(message), "*");
                postLogDetails({ uid, payLoad: `Message sent to parent successfully` });
            } catch (error) {
                postLogDetails({ uid, payLoad: `Error sending message to parent: ${error.message}` });
            }
        }
    };

    // useEffect(() => {
    //     if (!isLoading && !reshoot && !isActive && !isApiError) {
    //         navigate(`/smilewhite/result?uid=${uid}`);
    //     }
    // }, [isLoading, reshoot, isApiError]);

    useEffect(() => {
        if (isActive && isLoading) {
            const interval = setInterval(() => {
                setProgress((prev) => {
                    if (prev < 100) {
                        return prev + 1;
                    }
                    clearInterval(interval);
                    return prev;
                });
            }, 250);

            return () => clearInterval(interval);
        }
    }, [isActive, isLoading]);

    const submit = async () => {
        setLoading(true);
        setIsActive(true);
        const folderName = uid || "";

        // Debug logs
        // console.log('DEBUG: image', image);
        // console.log('DEBUG: croppedAreaPixels', croppedAreaPixels);
        // console.log('DEBUG: croppedBlob', croppedBlob, croppedBlob ? croppedBlob.size : null);

        if (!uid || !folderName) {
            setLoading(false);
            return;
        }

        const data = { uid, folderName, company };
        const encrypted = encrypt(data);

        if (!image) {
            setLoading(false);
            return;
        }

        if (!croppedBlob || croppedBlob.size < 10000) { // Arbitrary threshold for debugging
            alert('Image cropping failed or blob too small!');
            // console.error('Cropped blob is too small:', croppedBlob);
        }

        const file = new File([croppedBlob], "report_image.jpeg", { type: "image/jpeg", });

        const formData = new FormData();
        formData.append("report_image", file);
        formData.append("_n", encrypted);

        setReshoot(false);

        try {
            const uploadResponse = await simmetryImageUpload({ formData });

            if (uploadResponse.data?.data?.is_reshoot) {
                setLoading(false);
                setIsActive(false);
                setProgress(10);
                setImageError('');
                setReshoot(true);
                return;
            }

            const info = {
                uid,
                folderName,
                company,
                imageName: uploadResponse.data.data.imageName
            };

            setInfo("AI Processing...");
            localStorage.setItem("response", JSON.stringify(uploadResponse.data.data));

            // Flag to track if we should send success message
            let shouldSendSuccess = true;

            // Start second API call but don't wait for full completion
            const secondApiPromise = (async () => {
                try {
                    const response = await submitSimmetryScan(info);
                    const resData = response?.data?.data;

                    if (resData?.is_server_busy) {
                        shouldSendSuccess = false; // Cancel success message
                        setIsServerBusy(true);
                        setLoading(false);
                        setIsActive(false);
                        return;
                    }

                    if (resData?.is_reshoot) {
                        setProgress(10);
                        setImageError('');
                        setReshoot(true);
                        setLoading(false);
                        setIsActive(false);
                    } else {
                        setIsApiError(false);
                        setReshoot(false);

                    }

                } catch (err) {
                    const status = err?.response?.status;
                    postLogDetails({ uid, payLoad: `line 156 ${JSON.stringify(err)}` });

                    if (status === 429) {
                        shouldSendSuccess = false; // Cancel success message
                        setIsServerBusy(true);
                        setLoading(false);
                        setIsActive(false);
                        return;
                    }

                    postLogDetails({ uid, payLoad: `line 164 ${JSON.stringify(err)}` });

                    setLoading(false);
                    setIsApiError(true);
                    setImageError(status === 500 ? "ML failure" : '');
                    setProgress(10);
                    setReshoot(true);
                    setIsActive(false);
                }
            })();

            // Wait 2 seconds to check for immediate server busy response
            setTimeout(() => {
                if (shouldSendSuccess) {
                    sendMessageToParent({ result: true });
                }
            }, 2000);

            // Let the second API continue in background without blocking
            secondApiPromise.catch(() => {
                // Already handled in the async function above
            });

        } catch (err) {
            const status = err?.response?.status;

            sendMessageToParent({
                result: false,
                error: {
                    status: status,
                    message: status === 500 ? "ML failure" : "Upload failed",
                    details: err.message
                }
            });

            // Show error popup for iframe users
            if (isInIframe()) {
                setShowErrorPopup(true);
            }

            if (status === 429) {
                setIsServerBusy(true);
                setLoading(false);
                setIsActive(false);
                return;
            }

            setImageError(status === 500 ? "ML failure" : '');
            postLogDetails({ uid, payLoad: `image upload error ${JSON.stringify(err)}` });
            setProgress(10);
            setReshoot(true);
            setIsApiError(true);
            setIsActive(false);
        } finally {
            setLoading(true);
        }
    };

   // Replace your showCroppedImage function with this:

const showCroppedImage = useCallback(async () => {
    try {
        // Check if image is provided
        if (!image) {
            // console.error('No image provided for cropping!');
            setImageError('No image found. Please retake.');
            setReshoot(true);
            setLoading(false);
            return;
        }

        // console.log('=== PROCESSING IMAGE ===');
        // console.log('Image length:', image.length);
        // console.log('Image preview:', image.substring(0, 50));

        // Call getCroppedImg with only the image parameter
        // Your getCroppedImg function signature is: getCroppedImg(imageSrc)
        const croppedImgBlob = await getCroppedImg(image);
        
        // console.log('=== CROPPED BLOB RESULT ===');
        // console.log('Blob exists:', !!croppedImgBlob);
        // console.log('Blob size:', croppedImgBlob?.size);
        // console.log('Blob type:', croppedImgBlob?.type);

        if (!croppedImgBlob) {
            // console.error('getCroppedImg returned null/undefined');
            setImageError('Image processing failed. Please retake.');
            setReshoot(true);
            setLoading(false);
            return;
        }

        if (croppedImgBlob.size < 1000) {
            // console.error('Cropped blob is too small:', croppedImgBlob.size, 'bytes');
            setImageError('Image too small after processing. Please retake.');
            setReshoot(true);
            setLoading(false);
            return;
        }

        const fileUrl = URL.createObjectURL(croppedImgBlob);
        setCroppedBlob(croppedImgBlob);
        setCroppedImage(fileUrl);
        setCropStatus(true);
        setShowFullPreview(true);
        
        // console.log('=== IMAGE PROCESSING COMPLETED ===');
        // console.log('Final blob size:', croppedImgBlob.size, 'bytes');
        
    } catch (error) {
        // console.error('Error during image processing:', error);
        setImageError('Image processing error. Please retake.');
        setReshoot(true);
        setLoading(false);
    } finally {
        setLoading(false);
    }
}, [image]); // Only depend on image, not croppedAreaPixels

// Also update the useEffect:
useEffect(() => {
    if (image) {
        // console.log('=== PREVIEW RECEIVED IMAGE ===');
        // console.log('Image prop length:', image?.length);
        // console.log('Image prop preview:', image?.substring(0, 50));
        showCroppedImage();
    }
}, [image]); // Only depend on image

useEffect(() => {
    // console.log('=== PREVIEW RECEIVED IMAGE ===');
    // console.log('Image prop length:', image?.length);
    // console.log('Image prop preview:', image?.substring(0, 50));
    if (image) {
        showCroppedImage();
    }
}, [image]);

    // Error Popup Component
    const ErrorPopup = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                <div className="text-center">
                    <div className="text-red-500 text-4xl mb-4">⚠️</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Upload Failed
                    </h3>
                    <p className="text-gray-600 mb-4">
                        There was an error processing your image. Please try again.
                    </p>
                    <button
                        onClick={() => setShowErrorPopup(false)}
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {isSuccess ? (
                <div>Whiten success</div>
            ) : (
                <>
                    {!isLoading && (
                        <div className="w-screen h-screen flex flex-col overflow-hidden">
                            <p className="text-center mt-1 text-lg font-jost text-[#022022] w-full sm600:text-2xl">
                                Preview
                            </p>
                            {/* Image container with controlled height and aspect ratio */}
                            <div className="flex-grow flex justify-center items-center px-4 max-h-[70vh]">
                                <div className="relative w-full h-full flex justify-center items-center">
                                    <img
                                        src={image}
                                        alt="Cropped Preview"
                                        className="max-w-full max-h-full rounded-3xl object-contain border-4 border-blue-50"
                                    />
                                </div>
                            </div>
                            {/* Button container fixed to the bottom without causing overflow */}
                            <div className="w-full mt-3 flex flex-col justify-center items-center pb-6">
                                <button
                                    className="w-44 rounded-full text-white bg-[#011E41] font-jost font-medium text-base tracking-[4%] py-2 px-2"
                                    onClick={submit}
                                    disabled={isLoading}
                                >
                                    CONTINUE
                                </button>
                                <button
                                    className="w-44 mt-3 rounded-full font-jost font-medium text-base tracking-[4%] text-[#011E41] py-2 px-2 bg-[#EDE85E]"
                                    onClick={() => setCamera(true)}
                                >
                                    RETAKE
                                </button>
                             
                            </div>
                        </div>
                    )}
                    {isActive && (
                        <div>
                            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                                <div className="flex flex-col items-center space-y-6">
                                    {/* Circle image container */}
                                    <div className="relative">
                                        <div className="w-32 h-32 rounded-full overflow-hidden">
                                            <img
                                                src={localStorage.getItem("is_form") ? TeethWhitening : ImageProcessing}
                                                alt="Avatar"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                    {/* Percentage Text */}
                                    <div className="text-xl font-semibold">{progress}%</div>
                                    {/* Gradient Progress Bar */}
                                    <div className="w-64 h-2 bg-gray-200 rounded-full">
                                        <div
                                            className="h-full rounded-full"
                                            style={{
                                                width: `${progress}%`,
                                                background: `linear-gradient(to right, #011E41, #8BB8E8)`
                                            }}
                                        ></div>
                                    </div>
                                    {/* Text */}
                                    <div className="text-lg text-gray-700 font-jost">
                                                                            {showBookButton ? (
                                                                                <div className=" px-4 flex flex-col items-center text-center justify-center">
                                                                                    <span className="text-xl font-bold">Ready! Book now to view your smile.</span>
                                                                                    <span className="text-base font-normal mt-1">Your new image will appear on the confirmation page after booking.</span>
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-lg font-medium text-center">Crafting your perfect smile...</span>
                                                                            )}

                                    </div>

                                    {showBookButton && (
                                        <div className="mt-8">
                                            <button
                                              className="w-full rounded-full text-white bg-[#011E41] font-jost font-medium text-base tracking-[4%] py-4 px-8"
                                                onClick={handleBookingClick}
                                            >
                                                BOOK CONSULTATION
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {reshoot && (
                        <ReshootPopup setReshoot={setReshoot} errorType={errorType} setCamera={setCamera} setDemo={setDemo} setLoading={setLoading} />
                    )}
                    {isServerBusy && (
                        <ServerBusyPopup onClose={() => {
                            setIsServerBusy(false);
                            setLoading(false);
                        }} />
                    )}
                    {showErrorPopup && <ErrorPopup />}
                </>
            )}
        </>
    );
};

export default Preview;